import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ASSETS } from "@/data/assets";

const PORTRAIT = ASSETS.portrait;

export default function About() {
  return (
    <div data-testid="page-about" className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-24">
      {/* Header */}
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.28em] text-accent">
            // About
          </p>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            A journey across{" "}
            <span className="text-muted-foreground">three industries</span>,
            one operating philosophy.
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-2xl leading-relaxed">
            My career has been a journey across Energy, Technology, and Medical
            Services. Every chapter has shaped who I am as a leader and brought
            me to this point.
          </p>
        </div>

        <div className="lg:col-span-5">
          <div className="relative aspect-[4/5] w-full max-w-sm ml-auto overflow-hidden border border-border">
            <img
              src={PORTRAIT}
              alt="Vincent Palomarez portrait"
              loading="eager"
              decoding="async"
              className="w-full h-full object-cover"
              style={{ objectPosition: "50% 15%" }}
            />
            <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-background/95 to-transparent">
              <p className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                Enterprise Sales Leader
              </p>
              <p className="text-sm font-medium">Vincent Palomarez</p>
            </div>
          </div>
        </div>
      </div>

      <div className="hairline-ai my-16" />

      {/* About — short intro */}
      <section
        data-testid="about-intro-section"
        className="grid lg:grid-cols-12 gap-10 mb-16"
      >
        <div className="lg:col-span-4">
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-accent">
            // About
          </p>
          <h2 className="mt-3 font-display font-black text-3xl tracking-tight leading-tight">
            Where I am today.
          </h2>
        </div>
        <div className="lg:col-span-8">
          <p className="text-lg text-muted-foreground leading-relaxed">
            After more than{" "}
            <span className="text-foreground font-medium">
              15 years leading enterprise sales teams
            </span>{" "}
            at companies like{" "}
            <span className="text-foreground font-medium">
              Baker Hughes and AspenTech
            </span>
            , and as Co-Founder of an industrial IoT company, I now partner
            with growth-stage and established organizations in energy and
            industrial technology. I'm open to selective fractional leadership
            and advisory roles where I can bring my experience in complex
            sales cycles, team building, and technology transitions.
          </p>
        </div>
      </section>

      <div className="hairline-ai mb-16" />

      {/* Story */}
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              The through‑line
            </p>
            <h2 className="mt-3 font-display font-black text-3xl tracking-tight leading-tight">
              People. Performance. Purpose.
            </h2>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              The industries change. The technology accelerates. The
              fundamentals — how humans decide, buy, and lead — do not.
            </p>
          </div>
        </div>

        <div className="lg:col-span-8 space-y-10">
          <section>
            <h3 className="font-display font-bold text-2xl tracking-tight">
              Energy — Where I Built My Foundation
            </h3>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Oil & Gas prepared me more than any other chapter. It was my
              longest tenure and the place where I truly learned what it takes
              to succeed in the professional world of sales and leadership. I
              navigated complex stakeholders, national‑scale projects, and
              sales cycles measured in years rather than quarters. This
              environment demanded extreme agility, the ability to work across
              diverse teams and cultures, and a relentless focus on results.
            </p>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              That foundation shaped everything that followed.
            </p>
          </section>

          <section>
            <h3 className="font-display font-bold text-2xl tracking-tight">
              Technology — Where Speed Became a Discipline
            </h3>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              I shaped my strategies in industrial software and IoT through my
              own startup, selling into complex environments where buyers were
              engineers, project managers, and procurement teams. Sales cycles
              often stretched for months, and value was measured in avoided
              downtime, increased throughput, and real operational impact.
            </p>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Those years taught me how to sell to skeptics, earn credibility
              through depth rather than promises, and move fast without
              breaking trust. But they also revealed the constant pressure of
              performing in systems that had not yet evolved with modern
              technology to support sales teams.
            </p>
          </section>

          <section>
            <h3 className="font-display font-bold text-2xl tracking-tight">
              Medical Services — Where Empathy Became the Edge
            </h3>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Medical Services put humans back at the center. Outcomes, care,
              and dignity are not optional line items. In a highly competitive
              landscape where value and cost drive most decisions, this work
              reshaped my understanding of success. It highlighted the critical
              need for true product differentiation — developing solutions that
              not only set you apart from competitors but also integrate people
              and technology as essential elements. In this environment, they
              are not just helpful — they are necessary to survive and to
              thrive.
            </p>
          </section>

          <section className="border border-border p-6 bg-secondary/40">
            <p className="font-mono-ui text-[10px] uppercase tracking-widest text-ai">
              // The synthesis
            </p>
            <h3 className="mt-2 font-display font-bold text-2xl tracking-tight">
              Corporate Athlete 2.0
            </h3>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Corporate Athlete 2.0 is my framework for the next chapter —
              sustainable leadership in the AI era. It combines the resilience
              I built in energy, the discipline I sharpened in industrial
              software, and the human‑first mindset I earned in medical
              services. It's built to compound. It's built to be lived, not
              just written.
            </p>
            <Link to="/corporate-athlete">
              <Button
                data-testid="about-explore-ca-button"
                variant="outline"
                className="mt-5 rounded-sm gap-2 group"
              >
                Explore the framework
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </section>
        </div>
      </div>
    </div>
  );
}
